import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { DynamicObjectType } from './dynamic-object-type.enum';
import { ISearchField } from './search-field.interface';
import { SearchFieldComponent } from './search-field/search-field.component';

@Component({
    selector: 'app-search-filter',
    templateUrl: './search-filter.component.html',
    styleUrls: ['./search-filter.component.scss'],
    // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchFilterComponent {
    @Output() search = new EventEmitter<any>();
    @Input() disabled = false;
    @Input() objectStore;

    @ViewChildren(SearchFieldComponent)
    appSearchFields: QueryList<SearchFieldComponent>;

    @ViewChild(NgForm) myForm: NgForm;

    // objectStore$ = this.route.data.pipe(
    //     map(routeValue => routeValue.parentMenu.data.parentMenu.data.id)
    // );

    value = { textFilter: '', exactMatch: true };

    searchableFields = {
        ContractIdentifier: {
            id: 'ContractIdentifier',
            name: 'Contract Identifier',
            type: DynamicObjectType.Number,
        },
        DocumentTitle: {
            id: 'DocumentTitle',
            name: 'Contract Name',
            type: DynamicObjectType.Text,
        },
        FoldersFiledIn: {
            id: 'FoldersFiledIn',
            name: 'Library',
            type: DynamicObjectType.Folder,
        },
        Creator: {
            id: 'Creator',
            name: 'Created By',
            type: DynamicObjectType.Text,
        },
        DateCreated: {
            id: 'DateCreated',
            name: 'Created Date',
            type: DynamicObjectType.Date,
        },
        ContractStatus: {
            id: 'ContractStatus',
            name: 'Contract Status',
            type: DynamicObjectType.Lookup,
            lookupName: 'ContractStatuses',
        },
        ContractStartDate: {
            id: 'ContractStartDate',
            name: 'Contract Start Date',
            type: DynamicObjectType.Date,
        },
        ContractEndDate: {
            id: 'ContractEndDate',
            name: 'Contract End Date',
            type: DynamicObjectType.Date,
        },
        ContractReviewDeadlineDate: {
            id: 'ContractReviewDeadlineDate',
            name: 'Review Deadline Date',
            type: DynamicObjectType.Date,
        },
        ContractDateOfLastSignature: {
            id: 'ContractDateOfLastSignature',
            name: 'Date of Last Signature',
            type: DynamicObjectType.Date,
        },
        ContractAgreementType: {
            id: 'ContractAgreementType',
            name: 'Type of Agreement',
            type: DynamicObjectType.Lookup,
            lookupName: 'ContractAgreementTypes',
        },
        ContractBusinessOwner: {
            id: 'ContractBusinessOwner',
            name: 'Business Owner',
            type: DynamicObjectType.BusinessOwner,
        },
        ContractUPI: {
            id: 'ContractUPI',
            name: 'Contract UPI',
            type: DynamicObjectType.Text,
        },
        ContractWetInk: {
            id: 'ContractWetInk',
            name: 'Location of Wet Ink',
            type: DynamicObjectType.Text,
        },
        ContractDepartment: {
            id: 'ContractDepartment',
            name: 'Department',
            type: DynamicObjectType.Text,
        },
        ContractParties: {
            id: 'ContractParties',
            name: 'Contract Parties',
            type: DynamicObjectType.Text,
        },
        ContractCMSID: {
            id: 'ContractCMSID',
            name: 'CMS ID',
            type: DynamicObjectType.Text,
        },
        ContractNoAlertRequired: {
            id: 'ContractNoAlertRequired',
            name: 'No Alert Required',
            type: DynamicObjectType.Boolean,
        },
        ContractType: {
            id: 'ContractType',
            name: 'Document Type',
            type: DynamicObjectType.Lookup,
            lookupName: 'ContractTypes',
        },
        ContractProducts: {
            id: 'ContractProducts',
            name: 'Product',
            type: DynamicObjectType.Lookup,
            lookupName: 'ContractProducts',
            gerritLookup: 'allProducts',
        },
        ContractMolecules: {
            id: 'ContractMolecules',
            name: 'Molecule',
            type: DynamicObjectType.Lookup,
            lookupName: 'ContractMolecules',
            gerritLookup: 'allMolecules',
        },
        ContractTerritories: {
            id: 'ContractTerritories',
            name: 'Territory',
            type: DynamicObjectType.Lookup,
            lookupName: 'ContractTerritories',
            gerritLookup: 'allTerritories',
        },
        AlertEmailAdresses: {
            id: 'AlertEmailAdresses',
            name: 'Notification Email Address',
            type: DynamicObjectType.Text,
        },
        AlertDueDate: {
            id: 'AlertDueDate',
            name: 'Notification Alert Date',
            type: DynamicObjectType.Date,
        },
        AlertMessageText: {
            id: 'AlertMessageText',
            name: 'Notification Reminder Text',
            type: DynamicObjectType.Text,
        },
        AlertTemplatename: {
            id: 'AlertTemplatename',
            name: 'Notification Type',
            type: DynamicObjectType.Lookup,
            lookupName: 'AlertTemplates',
        },
        AlertSendDate: {
            id: 'AlertSendDate',
            name: 'Notification Sent Date',
            type: DynamicObjectType.Date,
        },
        AlertTrackTask: {
            id: 'AlertTrackTask',
            name: 'Notification Track Task',
            type: DynamicObjectType.Boolean,
        },
        Unknown: {
            id: 'Unknown',
            name: 'Unknown',
            type: DynamicObjectType.Unknown,
        },
        ContractLegalCounsel: {
            id: 'ContractLegalCounsel',
            name: 'Legal Counsel',
            type: DynamicObjectType.LegalCounsel,
        },
    };

    valueKeys = (): ISearchField[] => {
        if (!this.value) {
            return [];
        }
        const keys = Object.keys(this.value).filter(v => v !== 'textFilter');
        const result = keys.map(v => this.searchableFields[v]);

        return result;
    };

    searchKeys = (): string[] => {
        if (!this.searchableFields) {
            return [];
        }
        const keys = Object.keys(this.searchableFields);
        return keys;
    };

    availableKeys = (): ISearchField[] => {
        if (!this.value) {
            return this.searchKeys().map(v => this.searchableFields[v]);
        }

        const currentKeys = Object.keys(this.value);

        return this.searchKeys()
            .filter(v => currentKeys.indexOf(v) < 0)
            .map(v => this.searchableFields[v])
            .sort((a, b) => (a.name > b.name ? 1 : -2));
    };

    constructor(
        private route: ActivatedRoute,
        private hostElement: ElementRef
    ) {}

    addField = () => {
        // console.log('AddField', this.myForm.valid);
        const newValue: ISearchField = {
            id: 'Unknown',
            name: 'Unknown',
            type: DynamicObjectType.Unknown,
        };
        this.value[newValue.name] = newValue;

        // console.log(this.myForm);
    };

    typeChanged = (old: ISearchField, newKey: ISearchField) => {
        delete this.value[old.id];
        this.value[newKey.id] = undefined;

        // console.log(this.appSearchFields);
    };

    fieldDeleted = (input: ISearchField) => {
        delete this.value[input.id];
    };

    reset = () => {
        this.value = { textFilter: '', exactMatch: true };
    };

    searchNow = input => {
        // console.log('SearchNow', this.myForm);
        if (this.myForm.valid) {
            this.search.emit(input);
        } else {
            // console.log('Invalid Form');
            this.appSearchFields.forEach(field => field.markAsTouched());
        }
    };

    test = input => {
        console.log(input);
    };
}
